package it.neckar.lizergy.model.stumps

import it.neckar.lizergy.model.configuration.quote.builder.InverterConfiguration
import it.neckar.lizergy.model.configuration.quote.builder.InverterConfiguration.InverterConfigurationId
import it.neckar.open.kotlin.lang.requireNotNull

/**
 * Entries stored before the id existed carry `null` and belong to the inverter at their list position.
 */
sealed interface InverterMessdatenEntry {
  val inverterConfigurationId: InverterConfigurationId?
}

/**
 * Two inverter configurations sharing one id would share one entry.
 */
fun List<InverterConfiguration>.requireUniqueInverterConfigurationIds() {
  val duplicateInverterConfigurationIds = groupingBy { it.id }.eachCount().filterValues { it > 1 }.keys
  require(duplicateInverterConfigurationIds.isEmpty()) { "InverterMessdaten are stored by inverter configuration id, but these ids occur more than once: $duplicateInverterConfigurationIds" }
}

/**
 * [inverterConfigurationIndex] is the inverter's position in `inverterConfigurations`, the key of id-less entries.
 */
fun <T : InverterMessdatenEntry> List<T>.entryFor(inverterConfigurationId: InverterConfigurationId, inverterConfigurationIndex: Int): T? {
  return indexOfEntryFor(inverterConfigurationId, inverterConfigurationIndex)?.let { this[it] }
}

/**
 * Replaces in place or appends, so every other id-less entry keeps its position and with it its inverter.
 */
fun <T : InverterMessdatenEntry> List<T>.withEntry(entry: T, inverterConfigurationIndex: Int): List<T> {
  val inverterConfigurationId = entry.inverterConfigurationId.requireNotNull { "InverterMessdaten to store must carry an inverterConfigurationId" }
  val index = indexOfEntryFor(inverterConfigurationId, inverterConfigurationIndex) ?: return this + entry
  return toMutableList().also { it[index] = entry }
}

private fun <T : InverterMessdatenEntry> List<T>.indexOfEntryFor(inverterConfigurationId: InverterConfigurationId, inverterConfigurationIndex: Int): Int? {
  val indexById = indexOfFirst { it.inverterConfigurationId == inverterConfigurationId }
  if (indexById >= 0) {
    return indexById
  }
  return inverterConfigurationIndex.takeIf { it in indices && this[it].inverterConfigurationId == null }
}
